import ConfirmReservation from "@/layouts/ConfirmReservation";
import Award from "@/components/Award";
import { Divider } from "@/components/ui/divider";
import ChooseVehicle from "@/layouts/ChooseVehicle";
import ChooseProtection from "@/layouts/ChooseProtection";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import ReservationTabMenu from "@/features/ReservationTabMenu";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  PickupReturnProps,
  PriceResultProps,
  ProtectionProps,
  PriceProps,
  PreAuthorizationProps,
  DriverProps,
  PriceResultAllExtra,
  ReservationSearchParamProps,
  StationResultProps,
} from "@/lib/type";
import HomeFilterBar from "@/features/HomeFilterBar";
import AppHeader from "@/layouts/AppHeader";
import { getAllStation, getPrice, postReservations } from "@/lib/service";
import { Loading } from "@/components/ui/loading";
import { Button } from "@/components/ui/button";
import WhatsAppFab from "@/components/WhatsAppFab";
import { format } from "date-fns";
import { serialize } from "v8";
import {
  getHour,
  getMinute,
  getTwoDigitDay,
  getTwoDigitMonth,
} from "@/lib/utils";
import { ONE_TIME_CHARGE_EXTRA_ITEMS } from "@/lib/constant";
import {
  extraTotalWithCountOnly,
  extraTotalWithRentalDays,
} from "@/helpers/totalExtraPrice";
import { toast, Toaster } from "sonner";
import { generateCheckoutJson } from "@/helpers/generateCheckoutJson";
import { extraPriceWithCountOnly, extraPriceWithRentalDays } from "@/helpers/singleExtraPrice";
import { useRegion } from "@/context/RegionContext";
import DeliveryServicePanel from "@/features/delivery/components/DeliveryServicePanel";
import { DeliverySelection } from "@/features/delivery/api/deliveryApi";
import { buildDeliverySummary } from "@/features/delivery/lib/deliveryCalculations.js";
const MAX_CHARGE_DAYS: Record<string, number> = {
  NVS: 10,
  BSEAT: 10,
  CSEAT: 10,
  YDS: 4,
};

function Reservation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { region } = useRegion();

  // Add ref to track if API calls are in progress
  const apiCallInProgress = useRef(false);
  const initialized = useRef(false);

  // Track which API URL the selected vehicle came from (for cross-region booking)
  const [selectedApiUrl, setSelectedApiUrl] = useState<string>();

  // Memoize searchData to prevent unnecessary re-renders
  const searchData = useMemo<ReservationSearchParamProps>(() => ({
    vehicleType: searchParams.get("vehicleType") ?? "",
    pickupLocation: searchParams.get("pickupLocation") ?? "",
    dropoffLocation: searchParams.get("dropoffLocation") ?? "",
    pickupYear: searchParams.get("pickupYear") ?? "",
    pickupMonth: searchParams.get("pickupMonth") ?? "",
    pickupDay: searchParams.get("pickupDay") ?? "",
    dropoffYear: searchParams.get("dropoffYear") ?? "",
    dropoffMonth: searchParams.get("dropoffMonth") ?? "",
    dropoffDay: searchParams.get("dropoffDay") ?? "",
    pickupHour: searchParams.get("pickupHour") ?? "",
    pickupMinute: searchParams.get("pickupMinute") ?? "",
    dropoffHour: searchParams.get("dropoffHour") ?? "",
    dropoffMinute: searchParams.get("dropoffMinute") ?? "",
    driverAge: searchParams.get("driverAge") ?? "",
    countryOfResidence: searchParams.get("countryOfResidence") ?? "",
    promoCode: searchParams.get("promoCode") ?? "",
  }), [searchParams]);

  const [pickUp, setPickUp] = useState<StationResultProps>();
  const [dropOff, setDropOff] = useState<StationResultProps>();
  const [filterData, setFilterData] = useState<PickupReturnProps>();
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [vehicle, setVehicle] = useState<PriceResultProps>();
  const [protection, setProtection] = useState<ProtectionProps>();
  const [extras, setExtras] = useState<PriceResultAllExtra[]>([]);
  const [review, setReview] = useState<PriceProps>();
  const [cardDetail, setCardDetail] = useState<PreAuthorizationProps>();
  const [driver, setDriver] = useState<DriverProps>({ country: region.countryName });
  const [flightNo, setFlightNo] = useState("");
  const [priceData, setPriceData] = useState<PriceResultProps[]>([]);
  const [rentalDays, setRentalDays] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [appliedDiscount, setAppliedDiscount] = useState<any>(null);
  const [deliverySelection, setDeliverySelection] = useState<DeliverySelection[]>([]);

  const deliveryFeeTotal = useMemo(() => {
    return deliverySelection.reduce(
      (sum, selection) => sum + buildDeliverySummary(selection.price).totalFee,
      0,
    );
  }, [deliverySelection]);

  const payableReview = useMemo(() => {
    if (!review) {
      return review;
    }

    return {
      ...review,
      price: review.price + deliveryFeeTotal,
    };
  }, [deliveryFeeTotal, review]);

  // Reset scroll position when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [step]);

  // Calculate rental days - memoized to prevent unnecessary recalculations
  const calculatedRentalDays = useMemo(() => {
    const {
      pickupYear,
      pickupMonth,
      pickupDay,
      dropoffYear,
      dropoffMonth,
      dropoffDay,
    } = searchData;

    if (
      pickupYear &&
      pickupMonth &&
      pickupDay &&
      dropoffYear &&
      dropoffMonth &&
      dropoffDay
    ) {
      const pickupDate = new Date(
        Number(pickupYear),
        Number(pickupMonth) - 1,
        Number(pickupDay)
      );
      const dropoffDate = new Date(
        Number(dropoffYear),
        Number(dropoffMonth) - 1,
        Number(dropoffDay)
      );

      const timeDifference = dropoffDate.getTime() - pickupDate.getTime();
      const calculatedDays = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
      
      return calculatedDays > 0 ? calculatedDays : 0;
    }
    return 0;
  }, [searchData.pickupYear, searchData.pickupMonth, searchData.pickupDay, 
      searchData.dropoffYear, searchData.dropoffMonth, searchData.dropoffDay]);

  // Set rental days only if priceData is empty (avoid overriding API data)
  useEffect(() => {
    if (priceData.length === 0 && calculatedRentalDays > 0) {
      console.log("Using manually calculated rental days (rounded up):", calculatedRentalDays);
      setRentalDays(calculatedRentalDays);
    }
  }, [calculatedRentalDays, priceData.length]);

  // Memoized station fetching function
  const getStationList = useCallback(async () => {
    if (apiCallInProgress.current) {
      console.log("Station list call already in progress, skipping...");
      return;
    }
    
    try {
      apiCallInProgress.current = true;
      const stationList = await getAllStation();
      
      let tempPickup: StationResultProps | undefined;
      let tempDropoff: StationResultProps | undefined;

      stationList.forEach((station: StationResultProps) => {
        if (searchData.pickupLocation === station.rw_station_id) {
          tempPickup = station;
        }
        if (searchData.dropoffLocation === station.rw_station_id) {
          tempDropoff = station;
        }
      });

      setPickUp(tempPickup);
      setDropOff(tempDropoff);
      
      if (tempPickup && tempDropoff) {
        setFilterData({
          vehicleType: searchData.vehicleType,
          pickUpDate: new Date(
            `${searchData.pickupYear}-${searchData.pickupMonth}-${searchData.pickupDay}`
          ),
          pickUpTime: `${searchData.pickupHour}:${searchData.pickupMinute}`,
          pickUpLocation: tempPickup,
          returnDate: new Date(
            `${searchData.dropoffYear}-${searchData.dropoffMonth}-${searchData.dropoffDay}`
          ),
          returnTime: `${searchData.dropoffHour}:${searchData.dropoffMinute}`,
          returnLocation: tempDropoff,
          negoRate: searchData.promoCode,
          isSameReturn: searchData.pickupLocation === searchData.dropoffLocation,
          haveNegoRate: searchData.promoCode !== "",
          country: region.code,
          driverAge: searchData.driverAge,
        });
      } else if (searchData.pickupLocation && searchData.dropoffLocation) {
        // Station not found locally — still set filterData to trigger price fetch
        // (cross-region redirect will handle if the other region has it)
        setFilterData({
          vehicleType: searchData.vehicleType,
          pickUpDate: new Date(
            `${searchData.pickupYear}-${searchData.pickupMonth}-${searchData.pickupDay}`
          ),
          pickUpTime: `${searchData.pickupHour}:${searchData.pickupMinute}`,
          pickUpLocation: tempPickup || { rw_station_id: searchData.pickupLocation, name: searchData.pickupLocation } as any,
          returnDate: new Date(
            `${searchData.dropoffYear}-${searchData.dropoffMonth}-${searchData.dropoffDay}`
          ),
          returnTime: `${searchData.dropoffHour}:${searchData.dropoffMinute}`,
          returnLocation: tempDropoff || { rw_station_id: searchData.dropoffLocation, name: searchData.dropoffLocation } as any,
          negoRate: searchData.promoCode,
          isSameReturn: searchData.pickupLocation === searchData.dropoffLocation,
          haveNegoRate: searchData.promoCode !== "",
          country: region.code,
          driverAge: searchData.driverAge,
        });
      }
    } catch (error) {
      console.error("Error fetching station list:", error);
      setErrorMessage("Failed to load station data");
      setIsLoading(false);
    } finally {
      apiCallInProgress.current = false;
    }
  }, [searchData]);

  // Memoized price fetching function
  const getPriceList = useCallback(async () => {
    if (!filterData || apiCallInProgress.current) {
      console.log("Price list call skipped - no filterData or call in progress");
      return;
    }

    // Validate required data
    if (searchData.pickupLocation !== searchData.dropoffLocation && calculatedRentalDays < 3) {
      setIsLoading(false);
      setErrorMessage("Need to be more than 3 days");
      return;
    }

    try {
      apiCallInProgress.current = true;
      setIsLoading(true);
      
      const props = {
        countryId: region.countryId,
        pickUpDateTime:
          filterData?.pickUpDate == undefined
            ? `${searchData.pickupYear}-${searchData.pickupMonth}-${searchData.pickupDay} ${searchData.pickupHour}:${searchData.pickupMinute}`
            : `${format(filterData.pickUpDate ?? "", "yyyy-MM-dd")} ${filterData.pickUpTime}`,
        pickUpRentalStation:
          filterData?.pickUpLocation?.rw_station_id ??
          searchData.pickupLocation ??
          "",
        dropOffUpDateTime:
          filterData?.returnDate == undefined
            ? `${searchData.dropoffYear}-${searchData.dropoffMonth}-${searchData.dropoffDay} ${searchData.dropoffHour}:${searchData.dropoffMinute}`
            : `${format(filterData.returnDate ?? "", "yyyy-MM-dd")} ${filterData.returnTime}`,
        dropOffRentalStation:
          filterData?.returnLocation?.rw_station_id ??
          searchData.dropoffLocation ??
          "",
        driverAge: searchData.driverAge ? parseInt(searchData.driverAge) : undefined,
        countryOfResidence: searchData.countryOfResidence || undefined,
      };
      
      console.log("Fetching price list with props:", props);
      const priceList = await getPrice(props);

      // If all results are from the other region (local returned nothing),
      // redirect to the other region's website with the same search params
      const localResults = priceList.filter(v => !v._region || v._region === region.code);
      const otherResults = priceList.filter(v => v._region && v._region !== region.code);
      if (localResults.length === 0 && otherResults.length > 0) {
        const otherDomain = region.otherRegion.domain;
        const redirectParams = new URLSearchParams(Object.fromEntries(searchParams.entries()));
        window.location.href = `${otherDomain}/reservation?${redirectParams.toString()}`;
        return;
      }

      // Add this log to see the required extras for each vehicle in the price list
      priceList.forEach((vehicle, index) => {
        console.log(`Vehicle ${index + 1} required extras:`, vehicle.extras.required);
      });

      setPriceData(priceList);
      
      // Update rental days from API response if available
      if (priceList && priceList.length > 0 && priceList[0].duration) {
        const apiDays = priceList[0].duration.days || 0;
        const apiHours = priceList[0].duration.hours || 0;
        const totalDays = apiHours > 0 ? apiDays + 1 : apiDays;
        
        console.log("Setting rental days from API:", apiDays, "days and", apiHours, "hours, rounded up to:", totalDays);
        setRentalDays(totalDays);
      } else {
        console.log("API did not return duration data, using calculated days");
      }
    } catch (err) {
      const errorObj = err as { message: string };
      setErrorMessage(errorObj?.message);
      console.error("Error fetching price list:", err);
    } finally {
      setIsLoading(false);
      apiCallInProgress.current = false;
    }
  }, [filterData, searchData, calculatedRentalDays]);

  // Initialize data on component mount - single useEffect to control the flow
  useEffect(() => {
    if (!initialized.current && searchData.pickupLocation && searchData.dropoffLocation) {
      initialized.current = true;
      console.log("Initializing reservation data...");
      getStationList();
    }
  }, [searchData.pickupLocation, searchData.dropoffLocation, getStationList]);

  // Trigger price list fetch when filterData is set
  useEffect(() => {
    if (filterData && !apiCallInProgress.current) {
      console.log("FilterData changed, fetching price list...");
      getPriceList();
    }
  }, [filterData, getPriceList]);

  const reserve = async () => {
    if (
      filterData != undefined &&
      driver != undefined &&
      protection != undefined
      // &&
      // cardDetail != undefined
    ) {
      setIsLoading(true);
      // do the custom logic here extra
      const newExtras = [...extras].map((extra) => {
        return { code: extra.extra_id, count: extra.count };
      });
      const props = {
        PickupReturn: {
          pickUpDate: `${format(filterData?.pickUpDate ?? "", "yyyy-MM-dd")} ${filterData.pickUpTime}`,
          pickUpLocation: {
            rentalStation: pickUp?.rw_station_id ?? "",
            address: pickUp?.address.street ?? "",
            zipCode: pickUp?.address.postal_code ?? "",
            city: pickUp?.address.city ?? "",
            country: pickUp?.address.country ?? "",
            place: pickUp?.name ?? "",
            flight: flightNo,
          },
          returnDate: `${format(filterData?.returnDate ?? "", "yyyy-MM-dd")} ${filterData.returnTime}`,
          returnLocation: {
            rentalStation: dropOff?.rw_station_id ?? "",
            address: dropOff?.address.street ?? "",
            zipCode: dropOff?.address.postal_code ?? "",
            city: dropOff?.address.city ?? "",
            country: dropOff?.address.country ?? "",
            place: dropOff?.name ?? "",
          },
          negoRate: searchData.promoCode,
          isSameReturn: searchData.pickupLocation == searchData.dropoffLocation,
          haveNegoRate: searchData.promoCode != "",
          specialDelivery: false,
        },
        Driver: {
          ...driver,
          passportData: {
            number: "",
            date: "",
            place: "",
          },
        },
        discount_code: appliedDiscount?.code || undefined,
        // AdditionalDriver: {
        //   name: "Jane Smith",
        //   dateOfBirth: "1988-05-20",
        //   birthPlace: "City B",
        //   driverLicence: {
        //     number: "D987654321",
        //     date: "2016-07-15",
        //     expirationDate: "2026-07-15",
        //     place: "City B",
        //   },
        // },
        Payment: {
          // Base rental amount only — the server recomputes each delivery leg's
          // fee from the address and adds it authoritatively before charging.
          paymentAmount: review?.price ?? 0,
          reference: "",
          method: "stripe",
          paymentType: "CreditCard",
          currency: review?.currency ?? "",
        },
        // Delivery legs (no fee — the backend prices them). Empty when none chosen.
        delivery: deliverySelection.map((s) => ({
          type: s.type,
          customer_address: s.customer_address,
          customer_remark: s.customer_remark,
          customer_lat: s.customer_lat,
          customer_lng: s.customer_lng,
          scheduled_at: s.scheduled_at,
        })),
        // PreAuthorization: { ...cardDetail, creditCardType: "VISA" },
        // Rate: {
        //   rateCode: "SUMMER2024",
        //   package: "Family Package",
        // },
        Vehicle: {
          group_id: vehicle?.group_id ?? "",
          price: vehicle?.pricing.total_with_tax ?? 0,
        },
        Protection: protection,
        Review: {
          price: payableReview?.price ?? 0,
        },
        // here
        Extras: [
          ...extras.map((item) => {
            const isAddButtonOnly =
              item.is_insurance || item.extra_id === "NVS";
            return {
              ...item,
              quantity: isAddButtonOnly ? 1 : item.count,
              unit_price: item.value,
              tax_rate: item.tax_rate / 100,
              currency: item.currency,
              total_price:
                item.value * (item.count ?? 0) * (1 + item.tax_rate / 100),
              total_price_sum_days: ONE_TIME_CHARGE_EXTRA_ITEMS.includes(
                item.extra_id
              )
                ? extraPriceWithCountOnly(item).toFixed(2)
                : extraPriceWithRentalDays(item, rentalDays).toFixed(2),
            };
          }),
        ],
        //new
        // "Extras": [
        //   {
        //     "extra_id": "NVS",
        //     "name": "Navigational System(GPS)",
        //     "quantity": 1,
        //     "unit_price": 180.00,
        //     "tax_rate": 0.06,
        //     "total_price": 194.00,
        //     "currency": "MYR",
        //   },
        // ]
        // old
        //"Extras"
        // :[
        //   {
        //       "code": "CDW",
        //       "price": "10.00"
        //   },
        // ],
        Observations: "",
        CheckoutObj:
          generateCheckoutJson({
            car: vehicle!,
            total: payableReview!,
            dropOff,
            pickUp,
            extras: [
              ...extras.map((item) => {
                const isAddButtonOnly =
                  item.is_insurance || item.extra_id === "NVS";
                return {
                  ...item,
                  quantity: isAddButtonOnly ? 1 : item.count,
                  unit_price: item.value,
                  tax_rate: item.tax_rate / 100,
                  currency: item.currency,
                  total_price:
                    item.value * (item.count ?? 0) * (1 + item.tax_rate / 100),
                  total_price_sum_days: ONE_TIME_CHARGE_EXTRA_ITEMS.includes(
                    item.extra_id
                  )
                    ? extraPriceWithCountOnly(item).toFixed(2)
                    : extraPriceWithRentalDays(item, rentalDays).toFixed(2),
                };
              }),
            ],
            rentalDays,
          }) ?? {},
      };
      console.log("reservation", props);
      try {
        const result = await postReservations(props, selectedApiUrl);
        console.log("Reservation result:", result);
        
        if (result.status === "success" && result.data) {
          const redirectUrl = result.data.checkout_url || result.data.payment?.redirect_url;
          
          if (redirectUrl) {
            toast("Redirecting to payment page...");
            console.log("Redirecting to:", redirectUrl);
            window.location.href = redirectUrl;
          } else {
            console.error("No redirect URL found in response");
            toast("Payment URL not found. Please try again.");
          }
        } else {
          console.error("Reservation failed:", result);
          toast("Something went wrong, we not able to process your reservation, incident has been reported.");
        }
      } catch (error) {
        // If postReservations throws an error, handle it here
        console.error("Reservation error:", error);
        toast("Sorry, Something went wrong, we not able to process your reservation, incident has been reported.");
      } finally {
        setIsLoading(false);
      }


    }
  };
  const driverAge = useMemo(() => {
    // Parse driverAge safely
    return searchData.driverAge
      ? parseInt(searchData.driverAge.replace(/\D/g, ""), 10)
      : 0;
  }, [searchData.driverAge]);

  // Auto-add YDS (Young Driver Surcharge) when DOB indicates age < 23
  const YOUNG_DRIVER_MAX_AGE = 23;
  useEffect(() => {
    if (!driver?.dateOfBirth || !vehicle?.all_extras) return;

    const dob = new Date(driver.dateOfBirth);
    if (isNaN(dob.getTime())) return;

    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    const hasYds = extras.some((e) => e.extra_id === "YDS");
    const ydsExtra = vehicle.all_extras.find((e) => e.extra_id === "YDS");

    if (age < YOUNG_DRIVER_MAX_AGE && !hasYds && ydsExtra) {
      // Driver is young — auto-add YDS
      const updatedExtras = [...extras, { ...ydsExtra, count: 1 }];
      handleSelectExtras(updatedExtras);
    } else if (age >= YOUNG_DRIVER_MAX_AGE && hasYds) {
      // Driver is NOT young — remove YDS if it was auto-added
      const updatedExtras = extras.filter((e) => e.extra_id !== "YDS");
      handleSelectExtras(updatedExtras);
    }
  }, [driver?.dateOfBirth, vehicle?.all_extras]);

  // Memoize the onChange function to prevent unnecessary re-renders
  const handleDriverChange = useCallback((value: any) => {
    switch (true) {
      case !!value.name:
        setDriver({ ...driver, name: value.name });
        break;
      case !!value.birthDate:
        setDriver({ ...driver, dateOfBirth: value.birthDate });
        break;
      case !!value.email:
        setDriver({ ...driver, email: value.email });
        break;
      case !!value.address:
        setDriver({ ...driver, address: value.address });
        break;
      case !!value.city:
        setDriver({ ...driver, city: value.city });
        break;
      case !!value.postalCode:
        setDriver({ ...driver, postalCode: value.postalCode });
        break;
      case !!value.country:
        setDriver({
          ...driver,
          country: value.country ?? region.countryName,
        });
        break;
      case !!value.licenseNo:
        setDriver({
          ...driver,
          driverLicence: {
            ...driver?.driverLicence,
            number: value.licenseNo,
          },
        });
        break;
      case !!value.issuePlace:
        setDriver({
          ...driver,
          driverLicence: {
            ...driver?.driverLicence,
            place: value.issuePlace,
          },
        });
        break;
      case !!value.issueDate:
        setDriver({
          ...driver,
          driverLicence: {
            ...driver?.driverLicence,
            date: value.issueDate,
          },
        });
        break;
      case !!value.expDate:
        setDriver({
          ...driver,
          driverLicence: {
            ...driver?.driverLicence,
            expirationDate: value.expDate,
          },
        });
        break;
      case !!value.phoneNo:
        setDriver({ ...driver, phone: value.phoneNo });
        break;
      case !!value.flightNo:
        setFlightNo(value.flightNo);
        break;
      case !!value.cardNo:
        setCardDetail({
          ...cardDetail,
          creditCardNumber: value.cardNo,
        });
        break;
      case !!value.expDate:
        setCardDetail({
          ...cardDetail,
          creditCardDueDate: value.expDate,
        });
        break;
      default:
        break;
    }
  }, [driver, cardDetail]);

  // Memoize the onSelectExtras function to prevent unnecessary re-renders
  const handleSelectExtras = useCallback((newExtras: PriceResultAllExtra[]) => {
    setExtras(newExtras);
    const extraOneTimeChargeTotal = extraTotalWithCountOnly(
      newExtras.filter((e) =>
        ONE_TIME_CHARGE_EXTRA_ITEMS.includes(e.extra_id) && 
        e.extra_id !== "OW" && e.extra_id !== "AHOUR"
      )
    );
    const extraTimesRentalDaysTotal = extraTotalWithRentalDays(
      newExtras.filter(
        (e) => !ONE_TIME_CHARGE_EXTRA_ITEMS.includes(e.extra_id) &&
        e.extra_id !== "OW" && e.extra_id !== "AHOUR"
      ),
      rentalDays
    );
    const extraTotal =
      extraOneTimeChargeTotal + extraTimesRentalDaysTotal;
    console.log({
      total: {
        extraOneTimeChargeTotal,
        extraTimesRentalDaysTotal,
        extraTotal,
      },
    });
    console.log(
      "extraTimesRentalDays",
      newExtras.filter(
        (e) => !ONE_TIME_CHARGE_EXTRA_ITEMS.includes(e.extra_id)
      )
    );
    console.log(
      "extralWithCountOnly",
      newExtras.filter((e) =>
        ONE_TIME_CHARGE_EXTRA_ITEMS.includes(e.extra_id)
      )
    );
    console.log("all extras", vehicle?.all_extras);

    // HERE LOGIC
    setReview({
      price:
        (vehicle?.pricing.total_with_tax ?? 0) * rentalDays +
        (extraTotal ?? 0),
      currency: review?.currency ?? "",
    });
  }, [vehicle, rentalDays, review?.currency]);

  // Handle discount application
  const handleDiscountApplied = useCallback((discountData: any) => {
    setAppliedDiscount(discountData);
  }, []);

  return (
    <>
      <WhatsAppFab />
      <Toaster />
      <div
        className={
          priceData.length == 0 && !isLoading ? "visible" : "invisible"
        }
      >
        <div className="w-full h-screen bg-black/50 absolute z-40 flex flex-row justify-center items-center space-x-2">
          <div className="bg-white rounded-md flex flex-col text-center p-4">
            <span>{errorMessage ?? "No data could be found."}</span>
            <span className="mb-5">
              Please try again with other date/time/location.
            </span>
            <Button
              onClick={() => {
                navigate("/");
              }}
            >
              Back to search
            </Button>
          </div>
        </div>
      </div>
      <Loading className={isLoading ? "visible" : "invisible"} />
      {/* Header */}
      <AppHeader
        className="bg-neutral-100 pb-4 px-4 md:px-32 relative w-full z-30 "
        textColor="text-black"
        borderColor="border-black"
        hideMenu={true}
        isSmallLogo={true}
      />
      <div className="relative">
        {/* Home Filter Pop */}
        <div
          className={`absolute w-full transition duration-500 ${isOpen ? "h-full translate-y-0" : "-translate-y-full h-auto"}`}
          // onClick={() => setStep(previousStep)}
        >
          <HomeFilterBar
            className="mt-0 md:px-32"
            filterData={filterData}
            onChange={(data: PickupReturnProps) => {
              if (
                !data.pickUpLocation ||
                !data.returnLocation ||
                !data.pickUpDate ||
                !data.pickUpTime ||
                !data.returnDate ||
                !data.returnTime
              ) {
                return;
              }
              setFilterData(data);
              setStep(0);
              setVehicle(undefined);
              setProtection(undefined);
              setReview(undefined);
              setIsOpen(!isOpen);
            }}
          />
          <div
            className="bg-gray-500/40 h-full"
            onClick={() => setIsOpen(false)}
          ></div>
        </div>
        {/* Tab Menu */}
        <ReservationTabMenu
          currentStep={step}
          setStep={(value: number) => {
            switch (value) {
              case -1:
                setIsOpen(!isOpen);
                return;
              case 0:
                setVehicle(undefined);
                setProtection(undefined);
                setExtras([]);
                setDeliverySelection([]);
                setReview(undefined);
                setDriver({ country: region.countryName });
                setCardDetail(undefined);
                break;
              case 1:
                setProtection({
                  name: "Basic",
                  isInclude: true,
                  extraCount: 0,
                  price: 0,
                });
                setReview({
                  price: review?.price ?? vehicle?.pricing.total_with_tax ?? 0,
                  currency: review?.currency ?? vehicle?.pricing.currency ?? "",
                });
                setDriver({ country: region.countryName });
                setCardDetail(undefined);
                break;
              default:
                break;
            }

            setStep(value);
          }}
          filterData={filterData}
          vehicle={vehicle}
          protection={protection}
          extras={extras}
          review={payableReview}
          rentalDays={rentalDays}
          deliveryCount={deliverySelection.length}
          deliveryFee={deliveryFeeTotal}
        />
        {/* Body */}
        <div className={`py-4 ${step == 1 ? "" : "mx-4 md:mx-32"}`}>
          {step == 0 ? (
            <ChooseVehicle
              driverAge={driverAge}
              priceData={priceData}
              onSelect={(item: PriceResultProps) => {
                // If vehicle is from another region, redirect to that region's website
                if (item._region && item._region !== region.code) {
                  const otherDomain = region.otherRegion.domain;
                  const params = new URLSearchParams({
                    vehicleType: searchData.vehicleType || "car",
                    pickupLocation: searchData.pickupLocation || "",
                    dropoffLocation: searchData.dropoffLocation || "",
                    pickupYear: searchData.pickupYear || "",
                    pickupMonth: searchData.pickupMonth || "",
                    pickupDay: searchData.pickupDay || "",
                    dropoffYear: searchData.dropoffYear || "",
                    dropoffMonth: searchData.dropoffMonth || "",
                    dropoffDay: searchData.dropoffDay || "",
                    pickupHour: searchData.pickupHour || "",
                    pickupMinute: searchData.pickupMinute || "",
                    dropoffHour: searchData.dropoffHour || "",
                    dropoffMinute: searchData.dropoffMinute || "",
                    driverAge: searchData.driverAge || "",
                    countryOfResidence: searchData.countryOfResidence || "",
                  });
                  window.location.href = `${otherDomain}/reservation?${params.toString()}`;
                  return;
                }

                setVehicle(item);
                setSelectedApiUrl(item._apiUrl);
                setReview({
                  price:
                    (review?.price ?? item.pricing.total_with_tax) * rentalDays,
                  currency: review?.currency ?? item.pricing.currency,
                });
                setProtection({
                  name: "Basic",
                  isInclude: true,
                  extraCount: 0,
                  price: 0,
                });
                setStep(1);
              }}
              rentalDays={rentalDays}
            />
          ) : step == 1 && vehicle != undefined ? (
            <>
              {filterData && (
                <div className="mx-4 md:mx-32 mb-6">
                  <DeliveryServicePanel
                    pickupStation={pickUp}
                    returnStation={dropOff}
                    pickupScheduledAt={`${format(filterData.pickUpDate!, "yyyy-MM-dd")} ${filterData.pickUpTime}:00`}
                    returnScheduledAt={`${format(filterData.returnDate!, "yyyy-MM-dd")} ${filterData.returnTime}:00`}
                    currency={vehicle.pricing.currency}
                    currencySymbol={region.currencySymbol}
                    apiUrl={selectedApiUrl}
                    value={deliverySelection}
                    onChange={setDeliverySelection}
                  />
                </div>
              )}
              <ChooseProtection
                vehicleSelected={vehicle}
                protectionSelected={protection}
                extraSelected={extras}
                onNext={() => {
                  setProtection({
                    name: "Basic",
                    isInclude: true,
                    extraCount: 0,
                    price: 0,
                  });
                  setStep(2);
                }}
                onSelectExtras={handleSelectExtras}
                MAX_CHARGE_DAYS={MAX_CHARGE_DAYS}
                rentalDays={rentalDays}
                pickUp={pickUp}
                dropOff={dropOff}
                deliveryFeeTotal={deliveryFeeTotal}
              />
            </>
          ) : vehicle == undefined ? (
            <></>
          ) : (
            <>
              <ConfirmReservation
                car={vehicle}
                driverDetail={driver}
                cardDetail={cardDetail}
                review={payableReview}
                // locationName={pickUp?.name ?? ""}
                // datetime={`${format(filterData?.pickUpDate??'', 'yyyy-MM-dd')} ${filterData?.pickUpTime}`}
                pickUp={pickUp}
                dropOff={dropOff}
                onChange={handleDriverChange}
                extras={extras}
                rentalDays={rentalDays}
                onPay={reserve}
                onDiscountApplied={handleDiscountApplied}
                vehicleApiUrl={selectedApiUrl}
                delivery={deliverySelection
                  .filter((s) => s.price)
                  .map((s) => ({
                    type: s.type,
                    address: s.customer_address,
                    fee: buildDeliverySummary(s.price).totalFee,
                    currency: vehicle.pricing.currency,
                  }))}
              />
            </>
          )}
        </div>
        <Divider />
        <Award />
        {/* Footer */}
        <div className="bg-black grid md:grid-cols-8 md:px-32 px-4 text-sm">
        <span className="text-gray-400 py-8">@ Europcar {new Date().getFullYear()}</span>
        <div className="col-span-6 md:flex md:flex-row space-x-2 divide-x divide-white text-white font-bold justify-start py-8">
          <span className="cursor-pointer"><a href="#">Site Map</a></span>
          <span className="pl-2 cursor-pointer"><a href={`${region.productionUrl}/pages/legal-information`}>Legal Information</a></span>
          <span className="pl-2 cursor-pointer">
            <a href={`${region.productionUrl}/pages/pdpa`}>Cookies, Security and Privacy Policy</a>
          </span>
          <span className="pl-2 cursor-pointer"><a href={`${region.productionUrl}/pages/contact-us`}>Contact Us</a></span>
        </div>
        <div className="flex flex-row items-center gap-2 py-2">
        <a href="https://www.europcar.com/editorial/en" target="_blank"><img src="/small-logo-white.svg" className="w-[45px] h-[45px]" /></a>
        <a href="https://www.linkedin.com/company/europcar" target="_blank"><img src="/icon/linkedin.svg" className="w-[45px] h-[45px]" /></a>
        </div>
      </div>
      </div>
    </>
  );
}

export default Reservation;
